import { SetStateAction, useCallback, useMemo } from 'react'
import { NavigateOptions } from 'react-router'
import { useSearchParams } from 'react-router-dom'

class QueryParameters<T extends { [k: string]: any } = object> {
  handlers: any
  prefix: string

  constructor(handlers?: any, prefix?: string) {
    this.handlers = handlers || []
    this.prefix = prefix || ''
  }

  number<KEY extends string>(key: KEY): QueryParameters<T & { [k in KEY]: number }> {
    return new QueryParameters(
      [
        ...this.handlers,
        {
          fromUrlSearchParams(prefix: string, searchParams: URLSearchParams) {
            return [key, parseInt(searchParams.get(prefix + key) || '', 10) || 0]
          },
          setUrlSearchParams(prefix: string, searchParams: URLSearchParams, values: { [k in KEY]?: number }) {
            if (key in values) {
              if (values[key]) {
                searchParams.set(prefix + key, String(values[key]))
              } else {
                searchParams.delete(prefix + key)
              }
            }
          },
        },
      ],
      this.prefix
    )
  }

  boolean<KEY extends string>(key: KEY): QueryParameters<T & { [k in KEY]: boolean }> {
    return new QueryParameters(
      [
        ...this.handlers,
        {
          fromUrlSearchParams(prefix: string, searchParams: URLSearchParams) {
            return [key, Boolean(searchParams.get(prefix + key))]
          },
          setUrlSearchParams(prefix: string, searchParams: URLSearchParams, values: { [k in KEY]?: boolean }) {
            if (key in values) {
              if (values[key]) {
                searchParams.set(prefix + key, 'true')
              } else {
                searchParams.delete(prefix + key)
              }
            }
          },
        },
      ],
      this.prefix
    )
  }

  string<KEY extends string>(key: KEY): QueryParameters<T & { [k in KEY]: string }> {
    return new QueryParameters(
      [
        ...this.handlers,
        {
          fromUrlSearchParams(prefix: string, searchParams: URLSearchParams) {
            return [key, searchParams.get(prefix + key)]
          },
          setUrlSearchParams(prefix: string, searchParams: URLSearchParams, values: { [k in KEY]?: string }) {
            if (key in values) {
              if (values[key]) {
                searchParams.set(prefix + key, values[key]!)
              } else {
                searchParams.delete(prefix + key)
              }
            }
          },
        },
      ],
      this.prefix
    )
  }

  stringSet<KEY extends string>(key: KEY): QueryParameters<T & { [k in KEY]: string[] }> {
    return new QueryParameters(
      [
        ...this.handlers,
        {
          fromUrlSearchParams(prefix: string, searchParams: URLSearchParams) {
            return [key, searchParams.getAll(prefix + key)]
          },
          setUrlSearchParams(prefix: string, searchParams: URLSearchParams, values: { [k in KEY]?: string[] }) {
            if (key in values) {
              searchParams.delete(prefix + key)
              for (const value of values[key] || []) {
                searchParams.append(prefix + key, value)
              }
            }
          },
        },
      ],
      this.prefix
    )
  }

  commaSeparatedStringSet<KEY extends string>(key: KEY): QueryParameters<T & { [k in KEY]: string[] }> {
    return new QueryParameters(
      [
        ...this.handlers,
        {
          fromUrlSearchParams(prefix: string, searchParams: URLSearchParams) {
            return [key, (searchParams.get(prefix + key) || '').split(',').filter(Boolean)]
          },
          setUrlSearchParams(prefix: string, searchParams: URLSearchParams, values: { [k in KEY]?: string[] }) {
            if (key in values) {
              if (values[key]?.length) {
                searchParams.set(prefix + key, values[key]!.join(','))
              } else {
                searchParams.delete(prefix + key)
              }
            }
          },
        },
      ],
      this.prefix
    )
  }

  prefixed<PREFIX extends string>(
    prefix: PREFIX
  ): QueryParameters<{ [k in keyof T as k extends string ? `${PREFIX}${k}` : never]: T[k] }> {
    return new QueryParameters([...this.handlers], this.prefix + prefix)
  }

  fromUrlSearchParams(searchParams: URLSearchParams): T {
    return Object.fromEntries(
      this.handlers.map((handler: any) => handler.fromUrlSearchParams(this.prefix, searchParams))
    ) as T
  }

  nextUrlSearchParams(prevParams: URLSearchParams, nextValues: Partial<T>): URLSearchParams {
    const nextParams = new URLSearchParams(prevParams)
    for (const handler of this.handlers) {
      handler.setUrlSearchParams(this.prefix, nextParams, nextValues)
    }
    return nextParams
  }
}

export function queryParameters() {
  return new QueryParameters<object>()
}

export default function useQueryParameters<T extends object>(definition: QueryParameters<T>) {
  const [searchParams, setSearchParams] = useSearchParams()

  const values = useMemo<T>((() => definition.fromUrlSearchParams(searchParams)) as any, [definition, searchParams])

  const set = useCallback(
    (action: SetStateAction<Partial<T>>, options?: NavigateOptions) => {
      const next = typeof action === 'function' ? action(values) : action
      setSearchParams((prev) => definition.nextUrlSearchParams(prev, next), options)
    },
    [setSearchParams, values]
  )

  const toUrlSearchParams = useCallback(() => definition.nextUrlSearchParams(new URLSearchParams(), values), [values])

  return { ...values, set, toUrlSearchParams }
}
